/**
 * What the hand looks like over the board (docs/06-UI-SPEC.md §15).
 *
 * The pen is a pointing hand whose index fingertip is the exact point the ink comes
 * out of, so what the teacher aims at is what gets drawn. Beside it, on the overlay
 * layer, sits a faint ring the true size the brush will paint — the honest answer to
 * "how thick is this going to be", which no number beside a slider ever quite gives.
 */

import {
  canvasWidthToScreenWidth,
  screenWidthToCanvasWidth,
  type Point,
  type ToolId,
  type Viewport,
} from '@opal/shared';

import type { CanvasLayerName } from '@opal/contracts';

/** The artwork is a silhouette: white body, dark edge, so it shows on any paper. */
const CURSOR_BODY = '%23ffffff';
const CURSOR_EDGE = '%231a1a2e';

/**
 * Wraps a 24-unit path as a cursor image the browser can load inline.
 *
 * `detail` is drawn as a line on top of the silhouette rather than as part of it —
 * the eraser's band, which would disappear if it were filled along with the body.
 */
function cursorImage(path: string, detail = ''): string {
  const body =
    `%3Cpath d='${path}' fill='${CURSOR_BODY}' stroke='${CURSOR_EDGE}'` +
    ` stroke-width='1.4' stroke-linejoin='round'/%3E`;
  const band =
    detail === ''
      ? ''
      : `%3Cpath d='${detail}' fill='none' stroke='${CURSOR_EDGE}' stroke-width='1.2'/%3E`;
  return (
    `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='24' height='24'` +
    ` viewBox='0 0 24 24'%3E${body}${band}%3C/svg%3E")`
  );
}

/** A pointing hand, index finger extended. The flat of the tip is at (9.6, 2.2). */
export const HAND_PATH =
  'M9 2.2c-.9 0-1.6.7-1.6 1.6v9.6l-1.5-1.5c-.7-.7-1.7-.7-2.4 0c-.6.7-.6 1.7 0 2.4' +
  'l4.6 5c1 1.1 2.4 1.7 3.9 1.7h3.4c2.6 0 4.7-2.1 4.7-4.7v-4.6c0-.9-.7-1.6-1.6-1.6' +
  'c-.4 0-.8.2-1.1.4c-.2-.7-.9-1.2-1.6-1.2c-.5 0-.9.2-1.2.5c-.2-.7-.8-1.2-1.6-1.2' +
  'c-.4 0-.8.2-1.1.4V3.8c0-.9-.7-1.6-1.6-1.6z';

/** A marker held at an angle, chisel nib pointing down-left to (2.6, 21.4). */
export const MARKER_PATH = 'M14.4 6.6a2.2 2.2 0 0 1 3 3L7.6 19.4L2.6 21.4L4.6 16.4Z';

/**
 * A block eraser, tilted, centred on (12, 12) — the point it rubs from.
 *
 * SRS D2 asks for a high-definition eraser rather than a generic square, so it is
 * drawn as a real one: a rubber block with a band across it, held at an angle.
 */
export const ERASER_PATH =
  'M9.1 20.4 3.6 14.9a2 2 0 0 1 0-2.8l8.5-8.5a2 2 0 0 1 2.8 0l5.5 5.5a2 2 0 0 1 0 2.8' +
  'l-8.5 8.5a2 2 0 0 1-2.8 0Z';

/** The band across the eraser, drawn as a line so it stays visible on the silhouette. */
export const ERASER_BAND = 'M8.1 7.9 16.1 15.9';

/** docs/06-UI-SPEC.md §15 — the select tool is a plain arrow, tip at (3, 2). */
export const ARROW_PATH = 'M3 2.2 17.4 12.6l-6.2 1.1 3.4 6.6-2.6 1.3-3.4-6.6-4.6 4.2Z';

/**
 * The `cursor` value for a tool.
 *
 * The keyword after the image is what a browser that will not load an SVG cursor
 * falls back to; a crosshair still puts a precise point under the hand.
 */
export function cursorFor(tool: ToolId): string {
  if (tool === 'highlighter') return `${cursorImage(MARKER_PATH)} 3 21, crosshair`;
  // docs/06-UI-SPEC.md §15 — the eraser is held by its middle, which is where the
  // circle it rubs out is centred; the select tool is the plain arrow people expect.
  if (tool === 'eraser') return `${cursorImage(ERASER_PATH, ERASER_BAND)} 12 12, crosshair`;
  if (tool === 'select') return 'default';
  // docs/06-UI-SPEC.md §15 - a shape is placed by its corners, so the hand shows the
  // exact point the corner will land on and nothing else.
  if (tool === 'shape') return 'crosshair';
  // docs/06-UI-SPEC.md §15 — an I-beam where words go, and the laser's own red dot.
  if (tool === 'text') return 'text';
  if (tool === 'sticky') return 'crosshair';
  if (tool === 'laser') return `${LASER_CURSOR} 8 8, crosshair`;
  return `${cursorImage(HAND_PATH)} 10 2, crosshair`;
}

/**
 * The laser's cursor: a small red dot with a pale ring, so it is visible on ink.
 *
 * Written out rather than taken from `--laser`, because a cursor is an image the
 * browser loads by URL and an image cannot read a stylesheet. Kept beside the token
 * it copies (styles/tokens.css) so the two are changed together.
 */
const LASER_CURSOR =
  "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='16' height='16'" +
  " viewBox='0 0 16 16'%3E%3Ccircle cx='8' cy='8' r='6' fill='none'" +
  " stroke='%23ffffff' stroke-width='2' opacity='0.7'/%3E" +
  "%3Ccircle cx='8' cy='8' r='4' fill='%23ff2d55'/%3E%3C/svg%3E\")";

/**
 * How wide, on screen, the next stroke will actually be painted.
 *
 * Deliberately the same round trip the pen itself makes — screen width in, canvas
 * width stored, screen width back out (SRS C3) — so the ring can never drift away
 * from the ink it is promising.
 */
export function brushScreenWidth(viewport: Viewport, targetScreenWidth: number): number {
  return canvasWidthToScreenWidth(
    viewport,
    screenWidthToCanvasWidth(viewport, targetScreenWidth),
  );
}

/** How solid the ring is: present, but never competing with the work underneath. */
export const RING_ALPHA = 0.55;

/** Draws the size ring at a point on screen. Nothing is drawn without a point. */
export function drawSizeRing(
  ctx: CanvasRenderingContext2D,
  at: Point | null,
  screenWidth: number,
  color: string,
): void {
  if (!at) return;
  ctx.save();
  ctx.globalAlpha = RING_ALPHA;
  ctx.strokeStyle = color;
  ctx.lineWidth = 1;
  ctx.beginPath();
  ctx.arc(at.x, at.y, Math.max(screenWidth, 1) / 2, 0, Math.PI * 2);
  ctx.stroke();
  ctx.restore();
}

/** How solid the eraser's circle is: clearly there, because it decides what goes. */
export const ERASER_RING_ALPHA = 0.75;

/**
 * SRS C4 — the live circle showing exactly what the eraser will take.
 *
 * Drawn in two lines, dark over light, so the edge of the circle is readable over
 * black ink and over white paper alike.
 */
export function drawEraserRing(
  ctx: CanvasRenderingContext2D,
  at: Point | null,
  screenWidth: number,
  color: string,
): void {
  if (!at) return;
  const radius = Math.max(screenWidth, 2) / 2;
  ctx.save();
  ctx.globalAlpha = ERASER_RING_ALPHA;
  ctx.beginPath();
  ctx.arc(at.x, at.y, radius, 0, Math.PI * 2);
  ctx.strokeStyle = 'rgba(255,255,255,0.9)';
  ctx.lineWidth = 3;
  ctx.stroke();
  ctx.strokeStyle = color;
  ctx.lineWidth = 1.25;
  ctx.stroke();
  ctx.restore();
}

/**
 * Where each canvas sits in the stack.
 *
 * SRS C4 puts highlighter ink beneath pen ink. A finished highlighter stroke is
 * ordered correctly when the board layer paints, but the stroke still under the hand
 * lives on the active layer, which normally sits on top of everything finished. So
 * while the highlighter is the tool in hand, that one layer drops below the board —
 * and the line goes under the writing as it is drawn, not once the pen lifts.
 */
export function layerZIndex(layer: CanvasLayerName, tool: ToolId): number {
  const order: readonly CanvasLayerName[] =
    tool === 'highlighter'
      ? ['background', 'active', 'static', 'overlay']
      : ['background', 'static', 'active', 'overlay'];
  return order.indexOf(layer);
}
